// chat/actions.ts — the chat control EXECUTOR. The router (chat/brain.ts) maps ONE user
// message → an ARRAY of ChatActions (G17 — "do everything asked in one go"); this file
// EXECUTES each action against THIS app's on-device store (src/store.ts) or its billed
// brain endpoints (src/brain.ts).
//
// G16 (chat runs the app's OWN flagship features): the chat can START A PRACTICE
// (billed /questions call — paste a job posting straight into the chat and go), COACH AN
// ANSWER back-and-forth (billed /feedback call — the "as if I was in a terminal preparing"
// loop), and keep/curate the BEST-ANSWERS library, on top of search / progress / delete /
// backup / local copy / save-to-their-cloud.
//
// HITL discipline (mirrors Expense): destructive actions (delete_session, delete_best,
// backup_restore, import_file) are NEVER executed from a chat turn on their own — the Chat
// UI holds the FIRST destructive action and requires an explicit Confirm tap.

import {
  store, themeLabel, relativeDays,
  SENIORITY_LABELS, TYPE_LABELS, MODE_LABELS, THEME_NEXT_STEPS, PROFILE_RESUME_KEY,
  type Session, type Seniority, type InterviewType, type PracticeMode, type Feedback,
} from "../store";
import { exportBackup, importBackup } from "../backup";
import { invoke } from "../tauri-web";
import { saveCopyNow, importFromFile } from "../localFolder";
const LOCAL_SLUG = "interview-prep-tutor";
import { cloudBackupSave, cloudBackupLoad, cloudBackupStatus, cloudStoreSave, generateQuestions, getFeedback, BrainError } from "../brain";
import { addInstruction } from "./brain";
import { libraryAdd, libraryList, libraryRemove, libraryMove, libraryMerge } from "../library";

// A compact session row rendered inline in a chat bubble (filter results).
export interface FilterRow {
  id: string;
  role: string;
  seniority: string;
  interview_type: string;
  answered_count: number;
  question_count: number;
  created_at: string;
}

// A purely LOCAL filter over the user's own practice history (no server route).
export interface SessionFilter {
  /** Free-text contains over role. */
  text?: string;
  seniority?: string;
  interview_type?: string;
  /** Only sessions with unanswered questions. */
  inProgress?: boolean;
}

export type ChatAction =
  | { kind: "none"; message?: string }
  | { kind: "filter"; filter: SessionFilter; label?: string }
  | { kind: "progress" }
  | { kind: "delete_session"; id: string; label?: string } // UI confirms before this reaches executeAction
  // ── The flagship features, reachable from the chat (G16) ────────────────────
  | { kind: "start_practice"; role: string; seniority: Seniority; itype: InterviewType; mode: PracticeMode; company?: string; focus?: string; brief?: string } // billed: /questions
  | { kind: "coach_answer"; question: string; answer: string } // billed: /feedback
  | { kind: "save_best"; question: string; answer: string; note?: string }
  | { kind: "list_best" }
  | { kind: "delete_best"; id: string; label?: string } // UI confirms before executeAction
  // EDIT a kept answer in place. `question` identifies the existing row (save_best_answer upserts
  // by question); answer/note carry only what the customer actually changed. Not destructive —
  // it refines something they already kept, so no confirm gate.
  | { kind: "update_best"; question: string; answer: string; note: string; label?: string }
  | { kind: "save_to_cloud"; name?: string; source: "text" | "conversation" | "best_answers"; content?: string; provider?: "github" | "gdrive" }
  // ── Data portability + shaping ───────────────────────────────────────────────
  | { kind: "backup_save" }
  | { kind: "backup_status" }
  | { kind: "backup_restore"; provider?: "github" | "gdrive" } // UI confirms before executeAction (replaces all data)
  | { kind: "save_local_copy" }
  | { kind: "import_file" }
  | { kind: "remember"; instruction: string }
  // ── ARTIFACT TOOLS — the customer's library (fleet-wide) ────────────────────
  | { kind: "library_save"; title: string; itemKind: "lesson" | "plan" | "note"; content: string; folder?: string }
  | { kind: "library_list"; folder?: string }
  | { kind: "library_remove"; id: string; label?: string }   // UI confirms first
  | { kind: "library_move"; id: string; folder: string; label?: string }
  | { kind: "library_merge"; ids: string[]; title: string; label?: string };  // UI confirms first

export interface ActionResult {
  ok: boolean;
  message: string;   // shown back in the chat
  changed?: boolean; // the history changed → the main list should refresh
  rows?: FilterRow[]; // filter matches to render inline
}

const norm = (s: string) => s.trim().toLowerCase();

/** Apply a SessionFilter locally — every condition ANDs; empty filter matches all. */
export function applySessionFilter(sessions: Session[], f: SessionFilter): Session[] {
  return sessions.filter((s) => {
    if (f.seniority && s.seniority !== f.seniority) return false;
    if (f.interview_type && s.interview_type !== f.interview_type) return false;
    if (f.inProgress && s.answered_count >= s.question_count) return false;
    if (f.text && !norm(s.role).includes(norm(f.text)) && !norm(s.company).includes(norm(f.text))) return false;
    return true;
  });
}

const toRow = (s: Session): FilterRow => ({
  id: s.id,
  role: s.role,
  seniority: s.seniority,
  interview_type: s.interview_type,
  answered_count: s.answered_count,
  question_count: s.question_count,
  created_at: s.created_at,
});

/** Render structured coaching as chat text (mirrors the on-screen FeedbackView). */
function feedbackText(fb: Feedback): string {
  const parts: string[] = [];
  if (fb.strengths.length) parts.push("What worked:\n" + fb.strengths.map((s) => `• ${s}`).join("\n"));
  if (fb.gaps.length) parts.push("What to tighten:\n" + fb.gaps.map((s) => `• ${s}`).join("\n"));
  if (fb.star_note) parts.push(`Structure: ${fb.star_note}`);
  if (fb.better_example) parts.push(`One part of your answer, phrased stronger:\n"${fb.better_example}"`);
  parts.push('Edit your answer and send it again for another pass — or say "save that as a best answer" to keep it.');
  return parts.join("\n\n");
}

export async function executeAction(a: ChatAction): Promise<ActionResult> {
  try {
    switch (a.kind) {
      case "none":
        return { ok: true, message: a.message ?? "" };

      case "filter": {
        const all = await store.listSessions({ limit: 50000 });
        const matches = applySessionFilter(all, a.filter);
        const forLabel = a.label ? ` for "${a.label}"` : "";
        return {
          ok: true,
          message: `${matches.length} session${matches.length !== 1 ? "s" : ""}${forLabel}` + (matches.length > 50 ? " (showing first 50)" : ""),
          rows: matches.slice(0, 50).map(toRow),
        };
      }

      case "progress": {
        // Purely local aggregation of the user's OWN history — no billed call.
        const sessions = await store.listSessions({ limit: 50000 });
        const counts = await store.themeCounts();
        const best = await store.listBestAnswers();
        const answered = sessions.reduce((n, s) => n + s.answered_count, 0);
        if (sessions.length === 0) return { ok: true, message: "No practice yet — start a session from the main screen (or tell me the role here) and I'll track what the coaching keeps flagging." };
        const lines = [
          `${sessions.length} session${sessions.length !== 1 ? "s" : ""}, ${answered} answer${answered !== 1 ? "s" : ""} given${best.length ? `, ${best.length} best answer${best.length !== 1 ? "s" : ""} kept` : ""}. Last practiced ${relativeDays(sessions[0].created_at)}.`,
        ];
        if (counts.length > 0) {
          lines.push("What the coaching flags most:\n" + counts.slice(0, 5).map((c) => `• ${themeLabel(c.theme)} — ${c.count}×`).join("\n"));
          const next = THEME_NEXT_STEPS[counts[0].theme];
          if (next) lines.push(`Next step for ${themeLabel(counts[0].theme)}: ${next}`);
        }
        return { ok: true, message: lines.join("\n\n") };
      }

      case "delete_session":
        await store.deleteSession(a.id);
        return { ok: true, message: `Deleted ${a.label ?? "the session"}.`, changed: true };

      // ── Flagship: start a practice from the chat (ONE billed /questions call) ──
      case "start_practice": {
        // The saved resume (on-device profile) rides along like on the setup card, so
        // chat-started sessions get the same tailoring. Best-effort — "" when none kept.
        const resume = await store.getProfile(PROFILE_RESUME_KEY).then((v) => v ?? "").catch(() => "");
        const questions = await generateQuestions(a.role, a.seniority, a.itype, a.brief ?? "", resume);
        const session = await store.createSession({
          role: a.role, seniority: a.seniority, interview_type: a.itype,
          mode: a.mode, company: a.company ?? "", focus: a.focus ?? "", brief: a.brief ?? "",
          questions,
        });
        return {
          ok: true,
          message: `Started a ${MODE_LABELS[a.mode].toLowerCase()} session for ${session.role} — ${questions.length} questions ready. Open it from your sessions list to answer.`,
          changed: true,
        };
      }

      // ── Flagship: coach an answer back-and-forth in the chat (ONE billed /feedback call) ──
      case "coach_answer": {
        const recent = await store.listSessions({ limit: 1 });
        const role = recent[0]?.role ?? "";
        const seniority = (recent[0]?.seniority ?? "mid") as Seniority;
        // The latest session's briefing rides along for JD-relevance coaching (same as the room).
        const fb = await getFeedback(a.question, a.answer, role, seniority, recent[0]?.brief ?? "");
        return { ok: true, message: feedbackText(fb) };
      }

      case "save_best": {
        const recent = await store.listSessions({ limit: 1 });
        await store.saveBestAnswer(a.question, a.answer, recent[0]?.role ?? "", a.note ?? "");
        return { ok: true, message: "Saved to your best answers ★ — it's in the Best answers list on the main screen, and re-saving the same question updates it.", changed: true };
      }

      case "list_best": {
        const best = await store.listBestAnswers();
        if (best.length === 0) return { ok: true, message: 'No best answers kept yet — after coaching, tap "★ Save as best answer" (or tell me "save that as a best answer").' };
        const lines = best.slice(0, 10).map((b, i) => `${i + 1}. ${b.question}\n   ${b.answer.length > 140 ? b.answer.slice(0, 140) + "…" : b.answer}`);
        return { ok: true, message: `Your best answers (${best.length}):\n\n` + lines.join("\n\n") };
      }

      case "delete_best":
        await store.deleteBestAnswer(a.id);
        return { ok: true, message: `Removed ${a.label ?? "the best answer"} from your library.`, changed: true };

      case "update_best": {
        // saveBestAnswer REPLACES the row with the same question (db.rs: "Re-saving the same
        // question replaces the kept answer"), so this is a true in-place edit, not a duplicate.
        const recent = await store.listSessions({ limit: 1 });
        await store.saveBestAnswer(a.question, a.answer, recent[0]?.role ?? "", a.note);
        return { ok: true, message: `Updated ${a.label ?? "that best answer"}.`, changed: true };
      }

      // ── Save anything to the customer's OWN cloud (their GitHub/Drive; we retain nothing) ──
      case "save_to_cloud": {
        let content = a.content ?? "";
        let name = a.name ?? "note";
        if (a.source === "best_answers") {
          const best = await store.listBestAnswers();
          if (best.length === 0) return { ok: false, message: "No best answers to save yet — keep one first." };
          content = best.map((b) => `Q: ${b.question}\n\nA: ${b.answer}${b.note ? `\n\nNote: ${b.note}` : ""}`).join("\n\n---\n\n");
          name = a.name ?? "best-answers";
        }
        if (!content.trim()) return { ok: false, message: "Tell me what to save — a note, this conversation, or your best answers." };
        const { provider, file } = await cloudStoreSave(name, content, a.provider);
        const where = provider === "github" ? "GitHub" : provider === "gdrive" ? "Google Drive" : "cloud";
        return { ok: true, message: `Saved to your ${where} (${file}) — it's in your own storage; Oriora keeps nothing.` };
      }

      case "backup_save": {
        let provider: string;
        try {
          ({ provider } = await cloudBackupSave(JSON.parse(await exportBackup())));
        } catch (e) {
          // A backup past the standard path's size limit → the device-direct GitHub
          // save (any size, straight from this device, never through Oriora). Chat can DO this
          // one itself when GitHub is already connected in the backup menu; it can't run the
          // one-time OAuth login, so when not connected it points there by name instead.
          if (e instanceof BrainError && e.code === "too_large") {
            const connected = await invoke<boolean>("has_github_restore_token").catch(() => false);
            if (connected) {
              await invoke("backup_to_github_token");
              return { ok: true, message: "Your backup was too large for the standard path, so I saved it straight from this device to your GitHub — any size works that way, and it never passes through Oriora." };
            }
            return { ok: false, message: 'This backup is too large for the standard path. One-time fix: open the backup menu (☁ icon, top right) → "Log in with GitHub" — after that, large backups save directly from this device, and I can run them for you from chat.' };
          }
          throw e;
        }
        const name = provider === "github" ? "GitHub" : provider === "gdrive" ? "Google Drive" : "cloud";
        return { ok: true, message: "Backed up your whole practice history to your " + name + " — restore it on any device, any time." };
      }

      case "backup_status": {
        // The RESTORE STORY in words — same ground truth the panel's status line shows.
        const s = await cloudBackupStatus();
        if (s.connected.length === 0) {
          const deviceGh = await invoke<boolean>("has_github_restore_token").catch(() => false);
          if (deviceGh) {
            return { ok: true, message: "GitHub is logged in on this device (backup menu), so backups save and restore directly from here — any size, never passing through Oriora. Optionally, connecting a cloud at orioralabs.com → Settings → Connections adds the everyday path all your Oriora apps share." };
          }
          return { ok: true, message: 'Nothing is connected yet. Two options: connect GitHub or Google Drive once at orioralabs.com → Settings → Connections (all your Oriora apps back up there), or open the backup menu here (☁ icon) → "Log in with GitHub" — that one stays on this device only, and handles any size.' };
        }
        const name = (p: string) => (p === "gdrive" ? "Google Drive" : "GitHub");
        const when = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : null);
        const lines: string[] = [];
        const w = s.writes_to;
        if (w) {
          const slot = s.slots[w];
          lines.push(`Your backups write to ${name(w)} (connected in your Oriora account)` +
            (slot?.backup_exists ? ` — last backup ${when(slot.updated_at)}.` : " — no backup saved yet."));
        }
        for (const p of s.connected.filter((c) => c !== w)) {
          const slot = s.slots[p];
          lines.push(`${name(p)} is also connected but not used for new backups (${name(w!)} takes priority)` +
            (slot?.backup_exists ? ` — it still holds an older backup from ${when(slot.updated_at)}.` : "."));
        }
        const deviceGh = await invoke<boolean>("has_github_restore_token").catch(() => false);
        if (deviceGh) {
          lines.push("GitHub is also logged in on this device, so large backups save and restore directly from here — any size, never through Oriora.");
        }
        lines.push('Say "restore my sessions" and I\'ll read the backup back in — or name a source, like "restore from Drive".');
        return { ok: true, message: lines.join("\n") };
      }

      case "backup_restore": {
        const src = a.provider ? (a.provider === "gdrive" ? "Google Drive" : "GitHub") : "cloud";
        let st;
        try {
          st = await cloudBackupLoad(a.provider);
        } catch (e) {
          // Large-backup handoff: if GitHub is connected on this device, chat pulls it directly
          // itself (the user already confirmed the REPLACE before this executes); it can't run the
          // one-time OAuth login, so when not connected it points at the button by name instead.
          if (e instanceof BrainError && e.code === "unavailable") {
            const connected = await invoke<boolean>("has_github_restore_token").catch(() => false);
            if (connected && (!a.provider || a.provider === "github")) {
              await invoke("restore_from_github_token");
              return { ok: true, message: "Your backup was too large for the standard path, so I pulled it straight from your GitHub onto this device — your data is back.", changed: true };
            }
            return { ok: false, message: `Reading your ${src} backup through Oriora didn't work — if your backup is large, use the backup menu (☁ icon, top right) → "Log in with GitHub", which backs up and restores any size directly on this device.` };
          }
          throw e;
        }
        if (!st.connected) return { ok: false, message: "Connect GitHub or Google Drive in your Oriora account first (Settings → Service connections)." };
        if (!st.data) return { ok: false, message: a.provider ? `No backup found in your ${src} yet.` : 'No cloud backup found yet — say "back up" first.' };
        await importBackup(JSON.stringify(st.data));
        return { ok: true, message: `Restored from your ${st.provider === "gdrive" ? "Google Drive" : "GitHub"} backup.`, changed: true };
      }

      case "save_local_copy": {
        const saved = await saveCopyNow(LOCAL_SLUG);
        return saved
          ? { ok: true, message: "Saved a copy of your data to the folder you picked — it's yours to keep or move." }
          : { ok: true, message: "Cancelled — no copy saved." };
      }

      case "import_file": {
        const json = await importFromFile();
        if (json === null) return { ok: true, message: "No file chosen — nothing imported." };
        await importBackup(json);
        return { ok: true, message: "Imported your data from the file — this device now matches that backup.", changed: true };
      }

      case "remember": {
        addInstruction(a.instruction);
        return { ok: true, message: "Noted — from now on: " + a.instruction };
      }

      // ── ARTIFACT TOOLS (the library) — device-only, fleet-wide ──────────────
      case "library_save": {
        const _item = libraryAdd(a.title, a.itemKind, a.content, a.folder ?? "");
        return { ok: true, message: `Saved "${_item.title}" to your library${_item.folder ? ` (folder: ${_item.folder})` : ""} — on this device, yours.`, changed: true };
      }
      case "library_list": {
        const _items = libraryList(a.folder);
        if (!_items.length) return { ok: true, message: a.folder ? `Nothing in "${a.folder}" yet.` : "Your library is empty — ask me to write you something and I'll keep it here." };
        return { ok: true, message: (a.folder ? `In "${a.folder}":\n` : "Your library:\n") + _items.map((i) => `• ${i.title} — ${i.kind}${i.folder ? ` · ${i.folder}` : ""}`).join("\n") };
      }
      case "library_remove":
        return libraryRemove(a.id)
          ? { ok: true, message: `Removed ${a.label ?? "it"} from your library.`, changed: true }
          : { ok: false, message: "That item isn't in your library any more." };
      case "library_move":
        return libraryMove(a.id, a.folder)
          ? { ok: true, message: `Moved ${a.label ?? "it"} to "${a.folder}".`, changed: true }
          : { ok: false, message: "That item isn't in your library any more." };
      case "library_merge": {
        const _merged = libraryMerge(a.ids, a.title);
        return _merged
          ? { ok: true, message: `Merged into "${_merged.title}" — the originals are folded in.`, changed: true }
          : { ok: false, message: "I need at least two existing items to merge." };
      }


      default:
        return { ok: false, message: "I didn't catch that — try again?" };
    }
  } catch (e) {
    return { ok: false, message: `That didn't work: ${e instanceof Error ? e.message : String(e)}` };
  }
}

export { SENIORITY_LABELS, TYPE_LABELS };
